import { FC, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { DiffAction, FeeHudLineExport } from '../../../models/fees';
import { ComparisonHudLineDiff, HUD_LINE_COMPARABLE_FIELDS, PublicationComparison } from '../../../utils/feeComparisonDiff';
import {
  FieldDiffRow,
  TextCell,
  ValueCell,
  deleteFieldRows,
  humanizeFieldName,
  insertFieldRows,
  matchesRowSearch,
  rowClassName,
  unchangedFieldRows,
} from './diffTableHelpers';

interface HudLineDiffTabProps {
  comparison: PublicationComparison | null;
  showDiffsOnly: boolean;
  jsonDiffsOnly: boolean;
}

function hudLineRows(diff: ComparisonHudLineDiff, showDiffsOnly: boolean): FieldDiffRow[] {
  const label = diff.hudLine || '(unknown)';
  if (diff.action === DiffAction.Insert && diff.right) {
    return insertFieldRows(diff.right, HUD_LINE_COMPARABLE_FIELDS).map((r) => ({ key: `${label}-${r.field}-insert`, hudLine: label, kind: 'insert', ...r }));
  }
  if (diff.action === DiffAction.Delete && diff.left) {
    return deleteFieldRows(diff.left, HUD_LINE_COMPARABLE_FIELDS).map((r) => ({ key: `${label}-${r.field}-delete`, hudLine: label, kind: 'delete', ...r }));
  }
  if (diff.action === DiffAction.Unchanged) {
    if (showDiffsOnly || !diff.left) return [];
    return unchangedFieldRows(diff.left, HUD_LINE_COMPARABLE_FIELDS).map((r) => ({ key: `${label}-${r.field}-unchanged`, hudLine: label, kind: 'unchanged', ...r }));
  }
  return diff.changedFields.map((field) => ({
    key: `${label}-${field}`,
    hudLine: label,
    field: humanizeFieldName(field),
    aRaw: (diff.left as FeeHudLineExport | null)?.[field as keyof FeeHudLineExport],
    bRaw: (diff.right as FeeHudLineExport | null)?.[field as keyof FeeHudLineExport],
    kind: 'update',
  }));
}

export const HudLineDiffTab: FC<HudLineDiffTabProps> = ({ comparison, showDiffsOnly, jsonDiffsOnly }) => {
  const [first, setFirst] = useState(0);
  const [search, setSearch] = useState('');

  const rows = useMemo(
    () => (comparison?.hudLineDiffs ?? []).flatMap((d) => hudLineRows(d, showDiffsOnly)),
    [comparison, showDiffsOnly]
  );

  const filteredRows = useMemo(() => rows.filter((r) => matchesRowSearch(r, search)), [rows, search]);

  if (!comparison) {
    return <div className="text-sm text-gray-500 py-4">Select two publications to compare.</div>;
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-column align-items-center gap-2 py-6">
        <i className="pi pi-check-circle text-green-500 text-3xl" />
        <span className="text-base font-semibold">
          {showDiffsOnly ? 'No differences found' : 'No HUD lines on either publication'}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-column gap-3">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setFirst(0);
          }}
          placeholder="Search by HUD line, field, or value..."
          className="p-inputtext-sm border-1 border-solid border-gray-300"
        />
      </span>
      <DataTable
        key={String(jsonDiffsOnly)}
        value={filteredRows}
        stripedRows
        rowClassName={rowClassName}
        className="p-datatable-sm p-datatable-gridlines"
        tableStyle={{ tableLayout: 'fixed', width: '100%' }}
        emptyMessage="No results match your search"
        paginator
        rows={25}
        first={first}
        onPage={(e) => setFirst(e.first)}
        rowsPerPageOptions={[25, 50, 100]}
      >
        <Column header="HUD Line" style={{ width: '10%' }} bodyStyle={{ verticalAlign: 'top' }} body={(row: FieldDiffRow) => <TextCell value={row.hudLine} />} />
        <Column header="Field" style={{ width: '20%' }} bodyStyle={{ verticalAlign: 'top' }} body={(row: FieldDiffRow) => <TextCell value={row.field} />} />
        <Column header="A Value" style={{ width: '35%' }} bodyStyle={{ verticalAlign: 'top' }} body={(row: FieldDiffRow) => <ValueCell aRaw={row.aRaw} bRaw={row.bRaw} side="a" jsonDiffsOnly={jsonDiffsOnly} />} />
        <Column header="B Value" style={{ width: '35%' }} bodyStyle={{ verticalAlign: 'top' }} body={(row: FieldDiffRow) => <ValueCell aRaw={row.aRaw} bRaw={row.bRaw} side="b" jsonDiffsOnly={jsonDiffsOnly} />} />
      </DataTable>
    </div>
  );
};

export default HudLineDiffTab;
